"use client"

import type React from "react"
import { useState } from "react"
import { Col, Container, Row, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs"
import ExhibitorsList from "./ExhibitorsList"
import ParticipationsList from "./ParticipationsList"
import RepresentationsList from "./RepresentationsList"
import UserOrdersList from "./UserOrdersList"
import BadgeReprintList from "./BadgeReprintList" // Add this import
import PaymentModal from "./PaymentModal"
import UserOrderDetailsModal from "./UserOrderDetailsModal"
import type { ExhibitorVerification, Participation, PrintOrder } from "./badge-request"
import { toast } from "react-toastify"

const BadgeRequestContainer: React.FC = () => {
  const [activeTab, setActiveTab] = useState("1")
  const [currentStep, setCurrentStep] = useState<"exhibitors" | "participations" | "representations">("exhibitors")
  const [reprintStep, setReprintStep] = useState<"exhibitors" | "participations" | "representations">("exhibitors")
  const [selectedExhibitor, setSelectedExhibitor] = useState<ExhibitorVerification | null>(null)
  const [selectedParticipation, setSelectedParticipation] = useState<Participation | null>(null)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<PrintOrder | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(false)

  // Calculate total amount from order details
  const calculateOrderTotal = (order: PrintOrder): number => {
    if (!order.order_details) return 0
    return order.order_details.reduce((total, detail) => {
      return total + Number.parseFloat(detail.amount)
    }, 0)
  }

  const handleExhibitorSelect = (exhibitor: ExhibitorVerification) => {
    setSelectedExhibitor(exhibitor)
    if (activeTab === "1") {
      setCurrentStep("participations")
    } else if (activeTab === "2") {
      setReprintStep("participations")
    }
  }

  const handleParticipationSelect = (participation: Participation) => {
    setSelectedParticipation(participation)
    if (activeTab === "1") {
      setCurrentStep("representations")
    } else if (activeTab === "2") {
      setReprintStep("representations")
    }
  }

  const handleBackToExhibitors = () => {
    setSelectedExhibitor(null)
    setSelectedParticipation(null)
    if (activeTab === "1") {
      setCurrentStep("exhibitors")
    } else if (activeTab === "2") {
      setReprintStep("exhibitors")
    }
  }

  const handleBackToParticipations = () => {
    setSelectedParticipation(null)
    if (activeTab === "1") {
      setCurrentStep("participations")
    } else if (activeTab === "2") {
      setReprintStep("participations")
    }
  }

  const handleOrderCreated = (order: PrintOrder) => {
    try {
      const totalAmount = calculateOrderTotal(order)

      // Always refresh the orders list
      setRefreshTrigger((prev) => !prev)

      // Show appropriate success message
      let message = `Order ${order.order_number} created successfully!`

      if (order.is_postpaid) {
        message += " Payment will be processed later for postpaid account."
      } else if (totalAmount > 0) {
        message += ` Payment of ${totalAmount.toLocaleString()} RWF is required. You can pay from your orders list.`
      } else {
        message += " All badges are free."
      }

      toast.success(message)

      // Switch to orders tab and navigate back
      setTimeout(() => {
        setActiveTab("3") // Switch to My Orders tab
        handleBackToExhibitors()
      }, 2000)
    } catch (error) {
      console.error("Error in handleOrderCreated:", error)
      toast.error("There was an issue processing your order")
    }
  }

  const handleViewOrder = (order: PrintOrder) => {
    setSelectedOrder(order)
    setOrderDetailsModalOpen(true)
  }

  const handlePayOrder = (order: PrintOrder) => {
    setSelectedOrder(order)
    setPaymentModalOpen(true)
  }

  const handlePaymentSuccess = () => {
    setPaymentModalOpen(false)
    setSelectedOrder(null)
    setRefreshTrigger((prev) => !prev)
    toast.success("Payment submitted successfully!")
  }

  const handleCloseOrderDetails = () => {
    setOrderDetailsModalOpen(false)
    setSelectedOrder(null)
  }

  return (
    <>
      <Breadcrumbs mainTitle="Badge Print Request" parent="Badge Print Management" />
      <Container fluid>
        <Row>
          <Col md="12">
            {/* Enhanced Styled Tabs */}
            <div className="simple-tabs-container mb-4">
              <Nav className="simple-nav-tabs">
                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "1" ? "active" : ""}`}
                    onClick={() => setActiveTab("1")}
                  >
                    Request New Badges
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "2" ? "active" : ""}`}
                    onClick={() => setActiveTab("2")}
                  >
                    Request Badge Reprints
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "3" ? "active" : ""}`}
                    onClick={() => setActiveTab("3")}
                  >
                    My Orders
                  </NavLink>
                </NavItem>
              </Nav>
            </div>

            <TabContent activeTab={activeTab}>
              {/* New Badge Requests Tab */}
              <TabPane tabId="1">
                <div className="pt-3">
                  {currentStep === "exhibitors" && <ExhibitorsList onExhibitorSelect={handleExhibitorSelect} />}

                  {currentStep === "participations" && selectedExhibitor && (
                    <ParticipationsList
                      exhibitor={selectedExhibitor}
                      onParticipationSelect={handleParticipationSelect}
                      onBack={handleBackToExhibitors}
                    />
                  )}

                  {currentStep === "representations" && selectedParticipation && (
                    <RepresentationsList
                      participation={selectedParticipation}
                      onBack={handleBackToParticipations}
                      onOrderCreated={handleOrderCreated}
                    />
                  )}
                </div>
              </TabPane>

              {/* Badge Reprint Requests Tab */}
              <TabPane tabId="2">
                <div className="pt-3">
                  {reprintStep === "exhibitors" && <ExhibitorsList onExhibitorSelect={handleExhibitorSelect} />}

                  {reprintStep === "participations" && selectedExhibitor && (
                    <ParticipationsList
                      exhibitor={selectedExhibitor}
                      onParticipationSelect={handleParticipationSelect}
                      onBack={handleBackToExhibitors}
                    />
                  )}

                  {reprintStep === "representations" && selectedParticipation && (
                    <BadgeReprintList participation={selectedParticipation} onOrderCreated={handleOrderCreated} />
                  )}
                </div>
              </TabPane>

              {/* Orders Tab */}
              <TabPane tabId="3">
                <div className="pt-3">
                  <UserOrdersList onViewOrder={handleViewOrder} onPayOrder={handlePayOrder} refresh={refreshTrigger} />
                </div>
              </TabPane>
            </TabContent>
          </Col>
        </Row>

        {/* Payment Modal */}
        {paymentModalOpen && selectedOrder && (
          <PaymentModal
            isOpen={paymentModalOpen}
            toggle={() => setPaymentModalOpen(false)}
            order={selectedOrder}
            onSuccess={handlePaymentSuccess}
          />
        )}

        {/* User Order Details Modal */}
        {orderDetailsModalOpen && selectedOrder && (
          <UserOrderDetailsModal
            isOpen={orderDetailsModalOpen}
            toggle={handleCloseOrderDetails}
            order={selectedOrder}
          />
        )}
      </Container>

      {/* Enhanced Styles */}
      <style>{`
        .simple-tabs-container {
          border-bottom: 1px solid #e9ecef;
        }

        .simple-nav-tabs {
          border: none !important;
          background: transparent;
          gap: 2rem;
        }

        .simple-nav-link {
          border: none !important;
          background: transparent !important;
          padding: 1rem 0 !important;
          margin: 0 !important;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #6c757d !important;
          font-weight: 500;
          position: relative;
          border-radius: 0 !important;
        }

        .simple-nav-link:hover {
          color: #007bff !important;
          background: transparent !important;
        }

        .simple-nav-link.active {
          color: #007bff !important;
          background: transparent !important;
        }

        .simple-nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #007bff;
          border-radius: 2px 2px 0 0;
        }

        .simple-nav-link:hover::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #007bff;
          opacity: 0.5;
          border-radius: 1px 1px 0 0;
        }

        .simple-nav-link.active:hover::after {
          opacity: 1;
          height: 3px;
        }
      `}</style>
    </>
  )
}

export default BadgeRequestContainer
